import { useEffect, useState } from "react";

import { sourcesApiSlice } from "api/sourcesApi.slice";
import { isCustomBaseQueryError } from "api/utils";
import { CrossIcon, EditIcon, ErrorIcon, Spinner } from "common/components";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { activateModal } from "domains/modal/actions";
import * as Styled from "domains/moderate/sources/components/EditableSourceDetailSection/EditableSourceDetailSection.styled";
import { EditSourceForm } from "domains/moderate/sources/components/EditSourceForm/EditSourceForm";
import { SourceDetailSection } from "domains/moderate/sources/components/SourceDetailSection/SourceDetailSection";
import { getCanEditSources } from "entities/permissions/selectors";

type EditableSourceDetailSectionProps = {
  sourceId?: string;
};

export const EditableSourceDetailSection = ({ sourceId }: EditableSourceDetailSectionProps) => {
  const dispatch = useDispatch();
  const translate = useTranslate();

  const [isEditing, setIsEditing] = useState(false);

  const {
    data: source,
    isLoading,
    error,
  } = sourcesApiSlice.endpoints.getSourceById.useQuery({ sourceId: sourceId ?? "" }, { skip: !sourceId });

  useEffect(() => {
    disableEditing();
  }, [sourceId]);

  const enableEditing = () => setIsEditing(true);
  const disableEditing = () => setIsEditing(false);

  const openDeleteModal = () => {
    if (!sourceId) return;

    dispatch(activateModal({ id: "ConfirmDeleteSource", params: { sourceId } }));
  };

  if (!sourceId || (isCustomBaseQueryError(error) && error.status === 404))
    return (
      <Styled.SourceNotFoundDetailContainer>
        <Styled.SourceNotFoundContainer>
          <ErrorIcon style={{ color: "red" }} width={40} height={40} />
          <Styled.NotFoundTextContainer>
            <Styled.NotFoundTitle>{translate("app.moderate.sources.notFound.title")}</Styled.NotFoundTitle>
            <Styled.NotFoundMessage>{translate("app.moderate.sources.notFound.message")}</Styled.NotFoundMessage>
          </Styled.NotFoundTextContainer>
        </Styled.SourceNotFoundContainer>
      </Styled.SourceNotFoundDetailContainer>
    );

  if (isLoading)
    return (
      <Styled.LoadingIndicatorContainer>
        <Spinner />
      </Styled.LoadingIndicatorContainer>
    );

  return (
    <Styled.SourceDetailLayoutContainer>
      <EditableSourceDetailHeader
        sourceName={source?.name}
        isEditing={isEditing}
        onEditClick={enableEditing}
        onDeleteClick={openDeleteModal}
      />
      {isEditing ? (
        <EditSourceForm sourceId={sourceId} onCancel={disableEditing} onSubmitSuccess={disableEditing} />
      ) : (
        <SourceDetailSection sourceId={sourceId} />
      )}
    </Styled.SourceDetailLayoutContainer>
  );
};

type EditableSourceDetailHeaderProps = {
  sourceName?: string;
  isEditing: boolean;
  onEditClick: () => void;
  onDeleteClick: () => void;
};

const EditableSourceDetailHeader = ({
  sourceName,
  isEditing,
  onEditClick,
  onDeleteClick,
}: EditableSourceDetailHeaderProps) => {
  const canEditSources = useSelector(getCanEditSources);
  const translate = useTranslate();

  if (isEditing)
    return (
      <Styled.SourceDetailHeader>
        <Styled.SourceHeading>{sourceName}</Styled.SourceHeading>
      </Styled.SourceDetailHeader>
    );

  return (
    <Styled.SourceDetailHeader>
      <Styled.SourceHeading>{sourceName}</Styled.SourceHeading>
      <Styled.ButtonsContainer>
        <Styled.EditButton onClick={onEditClick} disabled={!canEditSources}>
          {translate("app.moderate.sources.edit.button")}
          <EditIcon width={14} height={14} />
        </Styled.EditButton>
        <Styled.DeleteButton onClick={onDeleteClick} disabled={!canEditSources}>
          {translate("app.moderate.sources.delete.button")}
          <CrossIcon width={14} height={14} />
        </Styled.DeleteButton>
      </Styled.ButtonsContainer>
    </Styled.SourceDetailHeader>
  );
};
